/*
 * global.js — shared helpers + base web components.
 * Loaded first, deferred. Exposes window.JF used by other modules.
 * No cross-asset ES imports (CDN-safe); coordination via document CustomEvents.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const JF = (window.JF = window.JF || {});

  JF.reduceMotion = reduceMotion;

  JF.fetchConfig = function (type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`,
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  };

  JF.debounce = function (fn, wait = 300) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  JF.money = function (cents) {
    try {
      return (cents / 100).toLocaleString(document.documentElement.lang || 'fr', {
        style: 'currency',
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'EUR',
      });
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  };

  /* Parse a section's HTML out of a Section Rendering API response. */
  JF.getSectionInnerHTML = function (html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  };

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary, details';

  JF.getFocusable = function (container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );
  };

  /* Minimal focus trap. Returns a cleanup function. */
  JF.trapFocus = function (container, elementToFocus) {
    const focusables = JF.getFocusable(container);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const target = elementToFocus || first || container;

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      if (!focusables.length) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    window.requestAnimationFrame(() => {
      if (target && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    });

    return function release() {
      container.removeEventListener('keydown', onKeydown);
    };
  };

  /* ---- Header: add shadow / compact when scrolled + expose pinned height as --header-h ---- */
  const header = document.querySelector('[data-site-header]');
  if (header) {
    // The pinned element is the sticky ancestor (`.shopify-section:has(> .header--sticky)`).
    let stickyEl = header;
    while (stickyEl && getComputedStyle(stickyEl).position !== 'sticky') stickyEl = stickyEl.parentElement;
    if (!stickyEl) stickyEl = header.closest('.shopify-section') || header;
    const setHeaderH = () => {
      document.documentElement.style.setProperty('--header-h', Math.round(stickyEl.getBoundingClientRect().height) + 'px');
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 24);
        setHeaderH();
        ticking = false;
      });
    };
    setHeaderH();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', setHeaderH, { passive: true });
  }

  /* ---- <menu-drawer> : off-canvas mobile navigation with focus trap ---- */
  class MenuDrawer extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.onKeyup = this.onKeyup.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-menu-dialog]');
      this.openers = document.querySelectorAll('[data-menu-open]');
      this.closers = this.querySelectorAll('[data-menu-close]');
      this.overlay = this.querySelector('[data-menu-overlay]');

      this.openers.forEach((btn) => btn.addEventListener('click', () => this.open(btn)));
      this.closers.forEach((btn) => btn.addEventListener('click', () => this.close()));
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    }

    open(opener) {
      this.opener = opener;
      this.setAttribute('open', '');
      document.documentElement.classList.add('is-drawer-open');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.addEventListener('keyup', this.onKeyup);
      this.releaseFocus = JF.trapFocus(this, this.querySelector('[data-menu-close]'));
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('is-drawer-open');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.removeEventListener('keyup', this.onKeyup);
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }

    onKeyup(e) {
      if (e.key === 'Escape') this.close();
    }
  }
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---- accessible disclosure for nested mobile menus (uses native details elsewhere) ---- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-collapse-toggle]');
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });

  /* ---- Scroll reveal + light parallax (IntersectionObserver) ---- */
  let revealObserver = null;

  /* Reveal [data-reveal] elements within a scope (default: whole document).
     Idempotent and safe to call again for content injected after load
     (Section Rendering API swaps, e.g. product recommendations) — otherwise
     those nodes are never observed and stay stuck at opacity:0. */
  JF.revealWithin = function (scope) {
    const root = scope || document;
    const els = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!els.length) return;
    if (
      JF.reduceMotion ||
      document.documentElement.classList.contains('anim-off') ||
      !('IntersectionObserver' in window)
    ) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              en.target.classList.add('is-revealed');
              obs.unobserve(en.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }
    // Reveal anything already in (or just below) the first viewport right away so
    // a page with a tall hero never paints blank below the fold; only deeper
    // content animates in on scroll.
    const vh = window.innerHeight || 800;
    els.forEach((el) => {
      if (el.getBoundingClientRect().top < vh * 1.15) {
        el.classList.add('is-revealed');
      } else {
        revealObserver.observe(el);
      }
    });
  };

  function initMotion() {
    JF.revealWithin(document);

    const parallax = Array.from(document.querySelectorAll('[data-parallax]'));
    if (parallax.length) {
      let ticking = false;
      const update = () => {
        const vh = window.innerHeight;
        parallax.forEach((el) => {
          const speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
          const r = el.getBoundingClientRect();
          const offset = (r.top + r.height / 2 - vh / 2) * -speed;
          el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
        });
        ticking = false;
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }, { passive: true });
      update();
    }

    // Announcement bar rotator — crossfade through messages (any count)
    const annTrack = document.querySelector('.announcement--rotating .announcement__track');
    if (annTrack) {
      const items = Array.from(annTrack.querySelectorAll('.announcement__item'));
      if (items.length > 1) {
        const aside = annTrack.closest('.announcement');
        const speed = (parseFloat(getComputedStyle(aside).getPropertyValue('--announcement-speed')) || 4) * 1000;
        let idx = items.findIndex((it) => it.classList.contains('is-active'));
        if (idx < 0) { idx = 0; items[0].classList.add('is-active'); }
        setInterval(() => {
          items[idx].classList.remove('is-active');
          idx = (idx + 1) % items.length;
          items[idx].classList.add('is-active');
        }, speed);
      }
    }
  }
  if (document.readyState !== 'loading') initMotion();
  else document.addEventListener('DOMContentLoaded', initMotion);
})();
